import { Criteria, CriteriaOperation, ErrorResponse } from '@/types/apiTypes';

export const isErrorResponse = (response: unknown): response is ErrorResponse => {
    return typeof response === 'object' && response !== null && 'error' in response;
};

/**
 * Extracts the typed backend {@link ErrorResponse} carried on an Error's `cause`.
 *
 * `makeFetchOrThrow` rejects with `new Error(message, { cause: errorResponse })`, so callers can recover
 * the structured error (status, message, validationErrors) to surface field-aware messages instead of a
 * generic failure toast.
 * @param error - The unknown value caught in a try/catch or mutation `onError`
 * @returns The backend ErrorResponse when present, otherwise undefined
 */
export const getErrorResponse = (error: unknown): ErrorResponse | undefined => {
    if (!(error instanceof Error)) {
        return undefined;
    }
    return isErrorResponse(error.cause) ? error.cause : undefined;
};

/**
 * Adds a criterion to the criteria array if the value is truthy
 * @param criteria - The criteria array to add to
 * @param op - The operation type (equals, like, etc.)
 * @param path - The field path
 * @param value - The value to filter by
 * @returns The modified criteria array (for chaining)
 */
export const addCriterion = <TData>(
    criteria: Criteria<TData>[],
    op: CriteriaOperation,
    path: keyof TData,
    value: any
): Criteria<TData>[] => {
    if (value !== undefined && value !== null && value !== '') {
        criteria.push({
            op,
            path,
            value,
        });
    }
    return criteria;
};

/**
 * Simplified version that works with string-based criteria (for legacy REST queries)
 * @param criteria - The criteria array to add to
 * @param op - The operation type (equals, like, etc.)
 * @param path - The field path as a string
 * @param value - The value to filter by
 * @returns The modified criteria array (for chaining)
 */
export const addSimpleCriterion = (
    criteria: Array<{ op: string; path: string; value: string }>,
    op: CriteriaOperation,
    path: string,
    value: any
): Array<{ op: string; path: string; value: string }> => {
    if (value !== undefined && value !== null && value !== '') {
        criteria.push({
            op,
            path,
            value,
        });
    }
    return criteria;
};

/**
 * Builds a namedCriteria entry for a named server-side search query.
 * Returns an empty array if search is empty or whitespace-only.
 * @param name - The named criteria key (e.g. 'lotSearch', 'bidderSearch')
 * @param search - The search string
 */
export const buildNamedSearchCriteria = (name: string, search?: string): Array<{ name: string; search: string }> => {
    if (!search || search.trim() === '') {
        return [];
    }
    return [{ name, search: search.trim() }];
};

/**
 * Builds a sort array from an optional sort param.
 * Returns an empty array if no sort is provided.
 * @param sort - Optional sort with direction and property
 */
export const buildSortArray = (sort?: {
    direction: 'ASC' | 'DESC';
    property: string;
}): Array<{ direction: 'ASC' | 'DESC'; property: string }> => {
    if (sort === undefined) {
        return [];
    }
    return [{ direction: sort.direction, property: sort.property }];
};

/**
 * Builds the API path for fetching taxonomy meta-type choices
 * @param metaTypeId - The ID of the meta type
 * @param pageNumber - Optional page number (1-based). If not provided or 1, no page parameter is added
 * @param pageSize - The number of items per page (default: 1000, max: 1000)
 * @returns The formatted API path
 */
export const buildMetaTypeChoicesPath = (metaTypeId: string, pageNumber?: number, pageSize: number = 1000): string => {
    const pageSizeParam = `pageSize=${Math.min(pageSize, 1000)}`;
    const pageParam = pageNumber && pageNumber > 1 ? `&pageNumber=${pageNumber}` : '';
    return `taxonomy/meta-type/${metaTypeId}/choices?${pageSizeParam}${pageParam}`;
};
